import type { NumberValue, SliderConfig } from '@lighthouse/core'
import { clamp } from 'rambda'
import React, { useCallback, useMemo, useState } from 'react'
import { useUpdateEffect } from 'react-use'
import styled from 'styled-components'

import { numberReg } from '../../../../constants'
import { useAppThemeContext } from '../../../ApplicationContainer'
import { applyFillPickerValue4CssColorValue } from '../../../FillPicker'
import BaseInput from '../../BaseInput'
import type { FieldBaseProps } from '../../types'
import { Slider } from './Slider'

interface SliderFieldProps extends FieldBaseProps {
    value?: NumberValue
    sliderConfig: SliderConfig
}

const SCxContainer = styled.div`
    width: 100%;
    padding: 4px 0px;
    border-radius: 6px;
`

const SCxSliderContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding-left: 3px;
`

function strToNumberStr(text?: string | number) {
    if (typeof text === 'string') {
        if (text === '') {
            return ''
        }
        if (numberReg.test(text)) {
            return Number(text)
        }
    }

    if (typeof text === 'number') {
        return text
    }

    return ''
}

export const SliderField: React.FunctionComponent<SliderFieldProps> = props => {
    const { readOnly, sliderConfig, onChange, value, config, isControlled } = props
    const { theme } = useAppThemeContext()
    const [isFocus, setIsFocus] = useState(false)
    const { slider, fieldPointer } = sliderConfig
    const { isInput } = slider ?? {}
    const { breakPoint } = config
    const placeholderColor = applyFillPickerValue4CssColorValue(breakPoint?.design?.placeholderColor?.color, theme.palettes)
    const backgroundColor = applyFillPickerValue4CssColorValue(breakPoint?.sliderDesign?.design?.background?.color, theme.palettes)

    const checkedColor = applyFillPickerValue4CssColorValue(breakPoint?.sliderDesign?.design?.color?.color, theme.palettes)

    const [min, max, step] = useMemo(() => {
        const { min = 0, max = 100, step = 1 } = slider ?? {}
        let newMin = min
        let newMax = max
        let newStep = step
        if (newMin < 0) {
            newMin = 0
        }
        if (newMax <= newMin) {
            newMax = newMin + 1
        }
        if (newStep > newMax - newMin) {
            newStep = 1
        }
        return [strToNumberStr(newMin), strToNumberStr(newMax), strToNumberStr(newStep)].map(Number)
    }, [slider])

    const [data, setData] = useState(strToNumberStr(value))

    const sliderData = useMemo(() => (data === '' ? 0 : Number(data)), [data])

    const handleSliderChange = useCallback(
        (value: number) => {
            setData(value)
            onChange?.({ type: 'slider', value })
        },
        [onChange]
    )

    const handleBlur = useCallback(
        (e: React.FocusEvent<HTMLTextAreaElement>) => {
            const numberValue = strToNumberStr(e.target.value)
            const newValue = numberValue === '' ? numberValue : clamp(min, max, numberValue)
            setData(newValue)
            setIsFocus(false)
            onChange?.({ type: 'slider', value: newValue })
        },
        [max, min, onChange]
    )

    const handleChange = useCallback((e: React.FocusEvent<HTMLTextAreaElement>) => {
        const newValue = e.target.value
        setData(newValue)
    }, [])

    useUpdateEffect(() => {
        setData(strToNumberStr(value))
    }, [value])

    return (
        <SCxContainer data-field-border={value !== ''} data-input-type="slider">
            <SCxSliderContainer>
                <Slider
                    min={min}
                    max={max}
                    step={step}
                    disabled={readOnly}
                    value={sliderData}
                    onChange={handleSliderChange}
                    color={checkedColor}
                    bgColor={backgroundColor}
                />
                {isInput && (
                    <BaseInput
                        data-field-input
                        data-field-active={isFocus}
                        data-field-disabled={!!readOnly}
                        placeholderColor={placeholderColor}
                        inputProps={{
                            type: 'number',
                            placeholder: '请输入',
                            min,
                            max,
                            onFocus: () => setIsFocus(true),
                            onChange: handleChange,
                            onBlur: handleBlur
                        }}
                        readOnly={readOnly}
                        style={{
                            width: 68,
                            height: 38
                        }}
                        value={data}
                    />
                )}
            </SCxSliderContainer>
        </SCxContainer>
    )
}
